import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { HasAccessGuard } from "./components/Permissions/HasAccessGuard.tsx";
import { AppRoute } from "./constants";
import { RolePermission } from "./graphql/generated.ts";
import Page404 from "./pages/404/Page404.tsx";
import ActivityManagementPage from "./pages/ActivityManagement/ActivityManagement.tsx";
import ActivityManagementDetailPage from "./pages/ActivityManagementDetail/ActivityManagementDetail.tsx";
import { AIAutomationPage } from "./pages/AIAutomation/AIAutomationPage.tsx";
import { AIResultPage } from "./pages/AIResult/AIResultPage.tsx";
import ClikDataPage from "./pages/ClikData/ClikDataPage.tsx";
import ClikDataCreatePage from "./pages/ClikDataCreate/ClikDataCreatePage.tsx";
import ClikDataDetailsPage from "./pages/ClikDataDetails/ClikDataDetailsPage.tsx";
import ClikResultPage from "./pages/ClikResult/ClikResultPage.tsx";
import CollectionDashboardPage from "./pages/CollectionDashboard/CollectionDashboardPage.tsx";
import CreditUnderwritingPage from "./pages/CreditUnderwriting/CreditUnderwritingPage.tsx";
import CustomerDatabasePage from "./pages/CustomerDatabase/CustomerDatabasePage.tsx";
import DeskCollectionDashboardPage from "./pages/DeskCollectionDashboard/DeskCollectionDashboardPage.tsx";
import MessageTemplatePage from "./pages/DeskCollectionMessageTemplate/MessageTemplatePage.tsx";
import DeskCollectionToolsPage from "./pages/DeskCollectionTools/DeskCollectionToolsPage.tsx";
import EmployeeCandidatePage from "./pages/EmployeeCandidateRequest/EmployeeCandidate.tsx";
import EmployeeCandidateRequestCreate from "./pages/EmployeeCandidateRequestCreate/EmployeeCandidateRequestCreate.tsx";
import ForgotPasswordPage from "./pages/ForgotPassword/ForgotPasswordPage.tsx";
import IDPDatabasePage from "./pages/IDPDatabase/IDPDatabasePage.tsx";
import IDPForgeryCheckPage from "./pages/IDPForgeryCheck/IDPForgeryCheckPage.tsx";
import KYCDetailPage from "./pages/KYC/KYCDetailPage.tsx";
import KYCPage from "./pages/KYC/KYCPage.tsx";
import LateFeeCalculatorPage from "./pages/LateFeeCalculator/LateFeeCalculatorPage.tsx";
import LimitRequestDetailPage from "./pages/LimitRequestDetail/LimitRequestDetailPage.tsx";
import LoginAccessDeniedPage from "./pages/Login/LoginAccessDeniedPage.tsx";
import LoginPage from "./pages/Login/LoginPage.tsx";
import { LoginPasswordlessPage } from "./pages/Login/LoginPasswordlessPage.tsx";
import { MainPage } from "./pages/MainPage/MainPage.tsx";
import PaymasterAnalysisPage from "./pages/PaymasterAnalysis/PaymasterAnalysisPage.tsx";
import ProfileSettingPage from "./pages/ProfileSetting/ProfileSettingPage.tsx";
import PromptEditorPage from "./pages/PromptEditor/PromptEditorPage.tsx";
import ReviewPage from "./pages/Review/ReviewPage";
import RiskFunnelDashboardPage from "./pages/RiskFunnelDashboard/RiskFunnelDashboardPage.tsx";
import RiskMonitoringPage from "./pages/RiskMonitoring/RiskMonitoringPage.tsx";
import RiskReviewPage from "./pages/RiskReview/RiskReviewPage.tsx";
import SubmissionListPage from "./pages/SubmissionList/SubmissionListPage.tsx";
import TenantManagementPage from "./pages/TenantManagement/TenantManagementPage.tsx";
import TradeInsurancePage from "./pages/TradeInsurance/TradeInsurancePage.tsx";
import UserAccessSettingPage from "./pages/UserAccessSetting/UserAccessSettingPage.tsx";
import { AuthGuard } from "./providers/AuthGuard.tsx";
import { CurrentUserProvider } from "./providers/CurrentUserProvider.tsx";
import { NoAuthGuard } from "./providers/NoAuthGuard.tsx";
import { UrqlProvider } from "./providers/UrqlProvider.tsx";
import { XRayProvider } from "./providers/XRayProvider.tsx";
import { theme } from "./utils/mantine/theme";

const router = createBrowserRouter(
  [
    {
      path: AppRoute.Home,
      element: (
        <AuthGuard>
          <MainPage />
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.LoginPasswordless}`,
      element: (
        <NoAuthGuard>
          <LoginPasswordlessPage />
        </NoAuthGuard>
      ),
    },
    {
      path: AppRoute.Login,
      element: (
        <NoAuthGuard>
          <LoginPage />
        </NoAuthGuard>
      ),
    },
    {
      path: AppRoute.AccessDenied,
      element: (
        <NoAuthGuard>
          <LoginAccessDeniedPage />
        </NoAuthGuard>
      ),
    },
    {
      path: AppRoute.UserAccessSetting,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewUserAccessSetting}>
            <UserAccessSettingPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.TenantManagement,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewUserAccessSetting}
            bababosTenantOnly
          >
            <TenantManagementPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.ProfileSetting,
      element: (
        <AuthGuard>
          <ProfileSettingPage />
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.ActivityManagement,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewActivityManagement}>
            <ActivityManagementPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.ActivityManagement}/:customerId`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewActivityManagement}>
            <ActivityManagementDetailPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.LateFeeCalculator}`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewActivityManagement}>
            <LateFeeCalculatorPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.ReviewAndApproval,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewReviewApproval}>
            <ReviewPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.CollectionDashboard,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewCollection}>
            <CollectionDashboardPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.ClikDataCreate,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanAddLimitRequest}>
            <ClikDataCreatePage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.ClikData,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewClikData}>
            <ClikDataPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.ClikData}/:id`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewClikData}>
            <ClikDataDetailsPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.EmployeeCandidate,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewEmployeeCandidateClik}
          >
            <EmployeeCandidatePage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.EmployeeCandidateCreate,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanEditEmployeeCandidateClik}
          >
            <EmployeeCandidateRequestCreate />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.ClikResult}/:id`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewClikResult}>
            <ClikResultPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.LimitRequest}/:id`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewBuyerInfoLimitInfo}>
            <LimitRequestDetailPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.SubmissionList,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewCreditUnderwriting}
            bababosTenantOnly
          >
            <SubmissionListPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.CreditUnderwriting,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewCreditUnderwriting}
            bababosTenantOnly
          >
            <CreditUnderwritingPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.RiskFunnelDashboard}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewRiskFunnelDashboard}
            bababosTenantOnly
          >
            <RiskFunnelDashboardPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.RiskMonitoring}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewRiskMonitoring}
            bababosTenantOnly
          >
            <RiskMonitoringPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.RiskReview}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewRiskReview}
            bababosTenantOnly
          >
            <RiskReviewPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.Customers}`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewCustomerDatabase}>
            <CustomerDatabasePage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.PropmtEditor}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewPrompt}
            bababosTenantOnly
          >
            <PromptEditorPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.RunAIAutomation}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewAiAutomation}
            bababosTenantOnly
          >
            <AIAutomationPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.AIResult}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewAiAutomation}
            bababosTenantOnly
          >
            <AIResultPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.PaymasterAnalysis}`,
      element: (
        <AuthGuard>
          <HasAccessGuard
            permission={RolePermission.CanViewPaymaster}
            bababosTenantOnly
          >
            <PaymasterAnalysisPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.IDPForgeryCheck}`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewPaymaster}>
            <IDPForgeryCheckPage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.IDPDatabase}`,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewPaymaster}>
            <IDPDatabasePage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.KYC}`,
      element: (
        <AuthGuard>
          <KYCPage />
        </AuthGuard>
      ),
    },

    {
      path: `${AppRoute.KYCDetail}`,
      element: (
        <AuthGuard>
          <KYCDetailPage />
        </AuthGuard>
      ),
    },
    {
      path: `${AppRoute.ForgotPassword}`,
      element: (
        <NoAuthGuard>
          <ForgotPasswordPage />
        </NoAuthGuard>
      ),
    },
    {
      path: AppRoute.DeskCollectionMessageTemplate,
      element: (
        <AuthGuard>
          <MessageTemplatePage />
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.DeskCollectionTools,
      element: (
        <AuthGuard>
          <DeskCollectionToolsPage />
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.DeskCollectionDashboard,
      element: (
        <AuthGuard>
          <DeskCollectionDashboardPage />
        </AuthGuard>
      ),
    },
    {
      path: AppRoute.TradeInsurance,
      element: (
        <AuthGuard>
          <HasAccessGuard permission={RolePermission.CanViewActivityManagement}>
            <TradeInsurancePage />
          </HasAccessGuard>
        </AuthGuard>
      ),
    },
    {
      path: "*",
      element: <Page404 />,
    },
  ],
  { basename: AppRoute.Home }
);

export default function App() {
  return (
    <UrqlProvider>
      <CurrentUserProvider>
        <MantineProvider theme={theme} withGlobalStyles withNormalizeCSS>
          <XRayProvider>
            <Notifications position="top-right" style={{ zIndex: 4000 }} />
            <RouterProvider router={router} />
          </XRayProvider>
        </MantineProvider>
      </CurrentUserProvider>
    </UrqlProvider>
  );
}
